import React, { useRef, useEffect, useCallback } from 'react';
import DataGrid, {
  Column,
  Paging,
  Pager,
  Button as GridButton,
  LoadPanel,
  Selection
} from 'devextreme-react/data-grid';
import notify from 'devextreme/ui/notify';

interface FiltriTenantProps {
  dataSource: any;
  onFilterSelect: (filter: any) => void;
  onFilterDelete: (id: number) => Promise<boolean>;
  selectedFilterId: number | null;
  componentRef?: (ref: any) => void;
  onContentReady?: (e: any) => void;
  onDataErrorOccurred?: (e: any) => void;
  refreshTrigger?: number;
}

// Interfaccia per i metodi esposti dal componente
interface FiltriTenantMethods {
  refreshData: () => void;
}

const FiltriTenant: React.FC<FiltriTenantProps> = ({ 
  dataSource, 
  onFilterSelect, 
  onFilterDelete, 
  selectedFilterId,
  componentRef,
  onContentReady,
  onDataErrorOccurred,
  refreshTrigger
}) => {
  // console.log("FiltriTenant render");
  
  // Riferimento alla griglia DataGrid
  const gridRef = useRef<any>(null);
  
  // Metodo per forzare il refresh dei dati
  const refreshData = useCallback(() => {
    // console.log('FiltriTenant.refreshData() chiamato');
    if (dataSource && typeof dataSource.load === 'function') {
      // console.log('Ricaricando i dati del DataGrid tramite dataSource.load()');
      // Forziamo un refresh completo della griglia
      dataSource.load().then(() => {
        if (gridRef.current && gridRef.current.instance) {
          try {
            // console.log('Aggiornamento visivo della griglia');
            gridRef.current.instance.repaint();
          } catch (error: unknown) {
            console.error('Errore durante l\'aggiornamento della griglia:', error);
          }
        } else {
          console.warn('Istanza della griglia non disponibile per l\'aggiornamento');
        }
      }).catch((error: unknown) => {
        console.error('Errore durante il caricamento dei dati:', error);
      });
    } else {
      console.warn('DataSource non disponibile o non valido');
    }
  }, [dataSource]);
  
  // Passa il riferimento della griglia e i metodi al componente padre
  useEffect(() => {
    if (componentRef) {
      // Creiamo un oggetto con i metodi che vogliamo esporre
      const methods: FiltriTenantMethods = {
        refreshData
      };
      
      componentRef({ ...gridRef.current, ...methods });
    }
  }, [componentRef, refreshData]);

  // Effetto per forzare il refresh della griglia quando refreshTrigger cambia
  useEffect(() => {
    // console.log('FiltriTenant useEffect[refreshTrigger] - Valore:', refreshTrigger);
    if (gridRef.current && gridRef.current.instance && typeof gridRef.current.instance.refresh === 'function') {
      try {
        gridRef.current.instance.refresh();
      } catch (error) {
        console.error('Errore durante il refresh della griglia:', error);
      }
    } else {
      // Puoi decommentare questo per debuggare se il refresh non avviene:
      // console.warn('Istanza della griglia non disponibile o refresh non è una funzione.');
    }
  }, [refreshTrigger]);

  // Gestore degli errori di dati (interno, se non fornito dall'esterno)
  const handleDataError = (e: any) => {
    console.error('Errore durante l\'operazione sulla griglia:', e);
    
    // Chiamiamo il gestore esterno se fornito
    if (onDataErrorOccurred) {
      onDataErrorOccurred(e);
    } else {
      // Altrimenti, gestione predefinita
      notify({
        message: `Errore: ${e.message || 'Si è verificato un errore durante l\'operazione'}`,
        type: 'error',
        displayTime: 3000,
        position: { at: 'top center', my: 'top center' }
      });
    }
  };

  // Gestore dell'evento dopo che il rendering del contenuto è completo (interno, se non fornito dall'esterno)
  const handleContentReady = (e: any) => {
    // console.log('DataGrid contentReady event');
    
    // Chiamiamo il gestore esterno se fornito
    if (onContentReady) {
      onContentReady(e);
    }
    // Altre azioni post-rendering se necessarie
  };

  return (
    <DataGrid
      ref={gridRef}
      dataSource={dataSource}
      showBorders
      height={300}
      repaintChangesOnly={true}
      // keyExpr="filtroId"
      selectedRowKeys={selectedFilterId ? [selectedFilterId] : []}
      onSelectionChanged={(e) => {
        if (e.selectedRowKeys.length > 0) {
          const selectedFilterId = e.selectedRowKeys[0];
          const selectedItems = e.selectedRowsData.filter((item: any) => item.id === selectedFilterId);
          if (selectedItems.length > 0) {
            onFilterSelect(selectedItems[0]);
          }
        }
      }}
      onContentReady={handleContentReady}
      onDataErrorOccurred={handleDataError}
      // onInitialized={() => console.log('DataGrid inizializzato')}
    >
      <LoadPanel enabled={true} />
      <Selection mode="single" />

      <Column type="buttons" width={110}>
        <GridButton
          icon="trash"
          hint="Elimina questo filtro dal server"
          onClick={async e => {
            const id = e.row?.data?.filtroId;
            if (id != null) {
              try {
                // Chiamiamo la funzione fornita dal componente padre
                const success = await onFilterDelete(id);
                if (success && dataSource && typeof dataSource.load === 'function') {
                  // Se l'eliminazione è andata a buon fine, ricarichiamo i dati
                  // dataSource.load(); // RIMOSSA - gestita dal padre
                }
              } catch (error) {
                console.error('Errore durante l\'eliminazione del filtro:', error);
              }
            }
          }}
        />

        <GridButton
          icon="eyeopen"
          hint="Seleziona"
          onClick={e => {
            const rowData = e.row?.data;
            if (rowData) {
              // Chiamiamo la funzione fornita dal componente padre
              onFilterSelect(rowData);
            }
          }}
        />
      </Column>

      <Column dataField="nome" caption="Nome filtro" />
      <Column
        dataField="espressione"
        caption="Espressione"
        cellRender={e => typeof e.value === 'string' ? e.value : JSON.stringify(e.value)}
        minWidth={200}
      />

      <Paging defaultPageSize={10} />
      <Pager
        showPageSizeSelector={true}
        allowedPageSizes={[5, 10, 20]}
        showInfo={true}
      />
    </DataGrid>
  );
};

export default React.memo(FiltriTenant);
